<script lang="ts">
  /**
   * Tax year — documents grouped by year of assessment, 1 March to end February.
   *
   * The tab exists because the one week a year somebody needs the IRP5, the medical
   * certificate and the RA certificate together is the week "search for it" is at
   * its worst: you do not know what you are looking for, you know only which year.
   *
   * **Nothing here is a total.** Vault knows what a certificate says; it does not
   * know what a return should claim, and a sum on this screen would be read as the
   * second thing by the only person who would ever add it up. The counts are counts
   * of documents, and they say so.
   */
  import { Icon, dateZA, Panel } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";
  import { inWords, inWordsLower, type Doc, type TaxYearGroup } from "../lib/model";
  import DocRow from "./DocRow.svelte";

  const { onopen }: { onopen: (id: string) => void } = $props();

  let years = $state<TaxYearGroup[] | null>(null);
  let undated = $state<Doc[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);

  async function load() {
    loading = true;
    try {
      const r = await api.get<{ years: TaxYearGroup[]; undated: Doc[] }>("/tax-years");
      years = r.years;
      undated = r.undated;
      error = null;
    } catch (e) {
      error = e instanceof ApiError ? e.message : "Could not read the tax years.";
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    void load();
  });
</script>

<Panel
  {loading}
  {error}
  isEmpty={!loading && !error && (years?.length ?? 0) === 0 && undated.length === 0}
  onretry={() => void load()}
  rows={4}
>
  {#snippet empty()}
    Nothing here belongs to a tax year yet. File an IRP5, a medical certificate, a retirement
    annuity certificate or an assessment, and Vault sorts them into years of assessment on its own.
  {/snippet}

  <p class="lede">
    Assessments, IRP5s, medical, retirement and statements, by year of assessment — 1 March to
    the end of February. Counts are documents, not amounts: Vault knows what each certificate
    says, not what a return should claim.
  </p>

  {#each years ?? [] as y (y.endYear)}
    <section class="yr">
      <header class="yr__head">
        <h2 class="yr__t">{y.label}</h2>
        <span class="yr__span">{dateZA(y.start)} to {dateZA(y.end)}</span>
        <span class="yr__n">{inWords(y.docs.length)} {y.docs.length === 1 ? "document" : "documents"}</span>
      </header>
      {#each y.docs as d (d.id)}
        <DocRow doc={d} {onopen} />
      {/each}
    </section>
  {/each}

  {#if undated.length > 0}
    <!-- Listed, never dropped. An undated certificate is the one most likely to
         matter and the one nobody would think to go looking for. -->
    <section class="yr">
      <header class="yr__head">
        <h2 class="yr__t">No date on them</h2>
        <span class="yr__span">Vault cannot place these in a year</span>
        <span class="yr__n yr__n--warn">
          <Icon name="alert" size={11} />
          {inWordsLower(undated.length)} to date
        </span>
      </header>
      {#each undated as d (d.id)}
        <DocRow doc={d} {onopen} />
      {/each}
    </section>
  {/if}
</Panel>

<style>
  .lede {
    margin: 0 0 var(--sp-4);
    font-size: var(--fs-small);
    color: var(--mut);
    line-height: 1.55;
    max-width: 62ch;
  }
  .yr + .yr {
    margin-top: var(--gap-section);
  }
  .yr__head {
    display: flex;
    align-items: baseline;
    gap: var(--sp-3);
    flex-wrap: wrap;
    padding-bottom: var(--sp-2);
    border-bottom: 1px solid var(--line);
  }
  .yr__t {
    margin: 0;
    font-size: var(--fs-h3);
    font-weight: 600;
    color: var(--tx);
  }
  .yr__span {
    font-size: var(--fs-micro);
    color: var(--mut);
  }
  .yr__n {
    margin-left: auto;
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--tx2);
  }
  /* Amber with a glyph and the words beside it — undated documents are the one
     thing on this screen anybody has to do something about. */
  .yr__n--warn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--warn);
  }
</style>
